import { Theme } from "../../../../providers/ThemeProvider";
import { StatusTone } from "../../utils/labPanelStatus";

/**
 * Layout for the platform's record listings.
 *
 * Extracted when Orders held two tables so they couldn't drift apart; now
 * shared by History's Assessments and Programming tables — same row card, same
 * header type scale, same accent and hover treatment. Column widths stay in
 * each screen, since the listings have different columns.
 */
const ordersTableStyles = {
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
    flex: 1,
    minHeight: 0,
  },
  searchWrapper: {
    flexShrink: 0,
  },
  filtersRow: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    flexShrink: 0,
    // The PNOĒ tab carries five filters; wrap rather than overflow on narrow
    // viewports (the lab-orders tab has four and never wraps).
    flexWrap: "wrap" as "wrap",
    rowGap: "8px",
  },
  tableHeader: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    padding: "0 16px",
    marginRight: "10px",
    border: "1px solid transparent",
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    flexShrink: 0,
    gap: "16px",
  }),
  tableBody: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
    paddingBottom: "24px",
  },
  tableRow: (theme: Theme, accent: string | null) => ({
    position: "relative" as "relative",
    // Clip the sliding "Review" tile when it's parked off the left edge.
    overflow: "hidden" as "hidden",
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    // Colored left accent on rows that need affiliate attention.
    borderLeft: accent
      ? `3px solid ${accent}`
      : `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    cursor: "pointer",
    gap: "16px",
  }),
  // The first cell of a row, which hosts the absolutely-positioned Review tile.
  firstCell: (colStyle: Record<string, any>) => ({
    position: "relative" as "relative",
    display: "flex",
    alignItems: "center",
    ...colStyle,
  }),
  // Full-height "Review" tile over the first cell signaling "action required".
  // Parked off the left edge (translateX(-100%)) and slides in on hover.
  reviewTile: (theme: Theme, tone: StatusTone, active: boolean) => ({
    position: "absolute" as "absolute",
    // Extend over the row's borders (3px left accent, 1px top/bottom) so the
    // tile is flush to the card edge; overflow:hidden on the row clips the
    // square left corners to the card's border-radius.
    left: "-3px",
    top: "-1px",
    bottom: "-1px",
    width: "171px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    // Left corners square (clipped flush by the card); right corners rounded.
    borderRadius: "0 8px 8px 0",
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    backgroundColor:
      tone === "danger" ? theme.colorTokens.D[70] : theme.colorTokens.W[70],
    transform: active ? "translateX(0)" : "translateX(-100%)",
    transition: "transform 0.25s ease",
    zIndex: 1,
  }),
  cellTextBase: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
  }),
  cellText: (theme: Theme, colStyle: Record<string, any>) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    overflow: "hidden" as "hidden",
    textOverflow: "ellipsis" as "ellipsis",
    whiteSpace: "nowrap" as "nowrap",
    ...colStyle,
  }),
  emptyMessage: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    textAlign: "center" as "center",
    padding: "48px 24px",
  }),
};

export default ordersTableStyles;
